import { isBrowser } from '@doltech/utils/lib/platform';
import { Popover, Spin } from 'antd';
import { motion, useMotionValue } from 'framer-motion';
import { isNil } from 'lodash';
import { Resizable } from 're-resizable';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { colorsV2 } from '../../../colors-v2';
import { TooltipAlignImage, TooltipAlignImageProps } from './TooltipAlignImage';
import { s3ToCDN } from '@doltech/utils/lib/url';

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
const DragHandle = styled.div`
  width: 3px;
  height: 48px;
  background-color: ${colorsV2.gray100};
  cursor: w-resize;
`;

const disableResizable = {
  top: false,
  right: false,
  bottom: false,
  left: false,
  topRight: false,
  bottomRight: false,
  bottomLeft: false,
  topLeft: false,
};

const defaultResizeableProps = {
  enable: {
    top: false,
    right: true,
    bottom: false,
    left: true,
    topRight: false,
    bottomRight: false,
    bottomLeft: false,
    topLeft: false,
  },
  handleComponent: {
    top: undefined,
    right: <DragHandle className="right" />,
    bottom: undefined,
    left: <DragHandle className="left" />,
    topRight: undefined,
    bottomRight: undefined,
    bottomLeft: undefined,
    topLeft: undefined,
  },
  minWidth: 200,
  handleWrapperClass: 'img-handle-wrapper',
  lockAspectRatio: true,
};

const Main = styled(Resizable)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  img {
    user-select: none;
    object-fit: cover;
    outline-offset: 1px;
    width: 100%;
    height: 100%;

    &:active,
    &:focus {
      outline: 2px solid ${colorsV2.black80};
    }
  }
  ${(p) =>
    !p.enable &&
    css`
      padding: 0;
      img {
        width: 100%;
        height: 100%;
      }
    `}
  .img-handle-wrapper {
    opacity: 0;
    transition: opacity 0.3s ease;
    > div {
      &.right {
        left: 20px;
      }
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  &:hover {
    .img-handle-wrapper {
      opacity: 1;
    }
  }

  .circle-spot {
    background-color: ${colorsV2.blue100};
    border-radius: 50%;
    position: absolute;
    width: 20px;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${colorsV2.white100};
    font-size: 10px;
  }
`;

interface CircleSpotProps {
  initX: number;
  initY: number;
  text: string;
  dragConstraints: any;
  onDragEnd: (input: any) => void;
  spotInfo: any;
  readOnly?: boolean;
}

interface CircleSpotInstance {
  getData: () => any;
  resizeStart: () => void;
  resizeKeepAspectRatio: (ratioX: number, ratioY: number) => void;
}

const CircleSpot = React.forwardRef<CircleSpotInstance, CircleSpotProps>((props, ref) => {
  const { initX, initY, text, dragConstraints, onDragEnd, spotInfo, readOnly = false } = props;
  const x = useMotionValue(initX);
  const y = useMotionValue(initY);

  const resizeX = useMotionValue(1);
  const resizeY = useMotionValue(1);

  React.useImperativeHandle(ref, () => {
    return {
      getData: () => {
        return {
          ...spotInfo,
          x: x.get(),
          y: y.get(),
        };
      },
      resizeStart: () => {
        resizeX.set(x.get());
        resizeY.set(y.get());
      },
      setNewRatio(newRatio) {
        x.set(x.get() * newRatio);
        y.set(y.get() * newRatio);
      },
      resizeKeepAspectRatio: (ratioX: number, ratioY: number) => {
        if (ratioX !== 0) {
          x.set(resizeX.get() + resizeX.get() * ratioX);
        }
        if (ratioY !== 0) {
          y.set(resizeY.get() + resizeY.get() * ratioY);
        }
      },
    };
  });

  return (
    <motion.div
      dragConstraints={dragConstraints}
      className="circle-spot"
      dragElastic={0}
      dragMomentum={false}
      style={{ x, y }}
      drag={!readOnly}
      onDragEnd={(event, info) => {
        onDragEnd({
          event,
          info,
          x,
          y,
        });
      }}
    >
      {text}
    </motion.div>
  );
});

interface ImageResizeabbleAlignableProps extends TooltipAlignImageProps {
  tooltipProps?: TooltipAlignImageProps;
  url: string;
  readOnly?: boolean;
  spots: any[];

  imageWidth: number;
  onSpotDragEnd?: (spot: any, params: any) => void;
  onResizeStop?: (width, spots) => void;
}

export const ImageResizableAlignable = (props: ImageResizeabbleAlignableProps) => {
  const {
    onClickAlignRight,
    onClickAlignCenter,
    onClickAlignLeft,
    onClickDelete,
    onReplaceImageSuccess,
    tooltipProps,
    url,
    readOnly = false,
    currentAlignment,
    imageWidth,
    spots,
    onResizeStop,
    onSpotDragEnd,
  } = props;
  const constraintsRef = React.useRef(null);
  const spotRefs = React.useRef({});
  const [uploading, setUploading] = React.useState(false);
  const [finalWidth, setFinalWidth] = React.useState(imageWidth);
  const [mobileRatio, setMobileRatio] = React.useState(1);
  const [resizableProps, setResizableProps] = React.useState<any>({
    ...defaultResizeableProps,
    maxWidth: 540,
  });

  React.useEffect(() => {
    if (isBrowser()) {
      const parentElm = document.getElementsByClassName(
        'diagram-label-completion-answer-result'
      )?.[0];
      let maxWidth = window.innerWidth - 24;
      if (parentElm) {
        maxWidth = parentElm.clientWidth - 48;
      }
      const width = Math.min(imageWidth, maxWidth);
      setFinalWidth(width);

      const ratio = (width || imageWidth) / imageWidth;
      setMobileRatio(ratio);
      setResizableProps((prevValue) => ({
        ...prevValue,
        size: {
          width: finalWidth,
          height: 'auto',
        },
        enable: readOnly ? disableResizable : true,
      }));
    }
  }, [finalWidth, imageWidth, readOnly]);

  React.useEffect(() => {
    if (spotRefs.current) {
      Object.keys(spotRefs.current).forEach((spotId) => {
        if (spotRefs.current?.[spotId]) {
          spotRefs.current[spotId].setNewRatio(mobileRatio);
        }
      });
    }
  }, [mobileRatio]);

  if (isNil(url)) return null;

  return (
    <Popover
      overlayClassName="align-image-popover"
      trigger={readOnly ? [] : ['focus', 'hover']}
      content={
        <TooltipAlignImage
          currentAlignment={currentAlignment}
          onClickAlignRight={onClickAlignRight}
          onClickAlignCenter={onClickAlignCenter}
          onClickAlignLeft={onClickAlignLeft}
          onClickDelete={onClickDelete}
          onReplaceImageUploadingStateChange={setUploading}
          onReplaceImageSuccess={onReplaceImageSuccess}
          {...tooltipProps}
        />
      }
    >
      <Spin spinning={uploading}>
        <Main
          className="img-resizable-alignable"
          {...resizableProps}
          onResizeStart={() => {
            if (spotRefs.current) {
              Object.values(spotRefs.current)
                .filter(Boolean)
                .forEach((spotRef: CircleSpotInstance) => {
                  spotRef.resizeStart();
                });
            }
          }}
          onResize={(_e, _direction, elementRef, d) => {
            const deltaRatioX = d.width / (elementRef.offsetWidth - d.width);
            const deltaRatioY = d.height / (elementRef.offsetHeight - d.height);
            if (spotRefs.current) {
              Object.values(spotRefs.current)
                .filter(Boolean)
                .forEach((spotRef: CircleSpotInstance) => {
                  spotRef.resizeKeepAspectRatio(deltaRatioX, deltaRatioY);
                });
            }
          }}
          onResizeStop={(_e, _direction, elementRef, d) => {
            if (spotRefs.current) {
              const newSpots = Object.values(spotRefs.current)
                .filter(Boolean)
                .map((spotRef: CircleSpotInstance) => spotRef.getData());
              onResizeStop(elementRef.offsetWidth, newSpots);
            }
          }}
        >
          <img
            loading="lazy"
            draggable="false"
            ref={constraintsRef}
            tabIndex={0}
            alt="i-resizable-alignable"
            src={s3ToCDN(url)}
          />
          {(spots || []).map((spot) => {
            const { x, y, id, text } = spot;
            return (
              <CircleSpot
                ref={(c) => {
                  spotRefs.current[id] = c;
                }}
                initX={x * mobileRatio}
                initY={y * mobileRatio}
                key={id}
                spotInfo={spot}
                readOnly={readOnly}
                dragConstraints={constraintsRef}
                onDragEnd={(params) => {
                  onSpotDragEnd(spot, params);
                }}
                text={text}
              />
            );
          })}
        </Main>
      </Spin>
    </Popover>
  );
};
